import { Badge, Button, Card, Center, Group, Loader, Stack, Text, ThemeIcon, Title } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconInfoCircle, IconPlus } from '@tabler/icons-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'
import type { Plan } from '@/services/api/api.types'

import { AutoRefillConfigModal } from './AutoRefillConfigModal'
import { CreditPurchaseModal } from './CreditPurchaseModal'
import { NoBillingAccount } from './NoBillingAccount'
import { PaymentMethodsSection } from './PaymentMethodsSection'

interface PaymentTabProps {
  availablePlans?: Plan[]
}

export function PaymentTab({ availablePlans = [] }: PaymentTabProps) {
  const { api } = useGlobalState()
  const queryClient = useQueryClient()

  const [showPlanModal, setShowPlanModal] = useState(false)
  const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false)

  // Query for billing info
  const billingInfoQuery = useQuery({
    queryKey: ['billing', 'info'],
    queryFn: () => api.getBillingInfo(),
  })

  // Extract billing data for component state
  const billingInfo = billingInfoQuery.data?.billingInfo
  const currentCredits = billingInfo?.packageCredits ?? 0
  const autoRefillEnabled = billingInfo?.autoRenewEnabled ?? false
  const paymentMethod = billingInfo?.paymentMethod ?? null
  const currentThresholdCredits = billingInfo?.lowBalanceThresholdCredits ?? null
  const currentPlan = availablePlans?.find(plan => plan.bundleId === billingInfo?.bundleId)

  const isLoading = billingInfoQuery.isLoading

  const handleAutoRefillUpdate = async () => {
    // Refresh billing info after auto-refill changes
    queryClient.invalidateQueries({ queryKey: ['billing', 'info'] })
  }

  const handleAddCredits = () => {
    if (availablePlans.length > 0) {
      setShowCreditPurchaseModal(true)
    } else {
      notifications.show({
        title: 'No Plans Available',
        message: 'Cannot purchase credits without available plans.',
        color: 'orange',
      })
    }
  }

  const handleCreditPurchase = async () => {
    // Refresh billing info after credit purchase completion
    queryClient.invalidateQueries({ queryKey: ['billing', 'info'] })
  }

  const formatCredits = (credits: number) => {
    return credits.toLocaleString()
  }

  const getAutoRefillDescription = () => {
    if (!autoRefillEnabled) {
      return 'You will need to manually add credits when your balance runs low'
    }

    if (!currentPlan) {
      return 'Your account will automatically purchase more credits when running low'
    }

    const threshold = billingInfo?.lowBalanceThresholdCredits
    const thresholdText = threshold ? formatCredits(threshold) : '0'

    return `When your balance reaches ${thresholdText} credits, your payment method will be charged $${currentPlan.priceDollars} and ${formatCredits(currentPlan.creditsPerBundle)} credits will be added to your account.`
  }

  // Handle loading state
  if (isLoading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    )
  }

  // Handle error state or no billing data
  if (billingInfoQuery.error || !billingInfo) {
    return <NoBillingAccount />
  }

  return (
    <Card shadow="sm" p="xl" radius="md" withBorder>
      <Stack gap="xl" px="20px">
        <div>
          <Title order={4} mb="xs">
            Payment Information
          </Title>
          <Text size="sm" c="dimmed">
            Manage your credit balance and payment preferences
          </Text>
          <Text size="sm" c="dimmed">
            1 credit per user request
          </Text>
        </div>

        {/* Credit Balance Section */}
        <Stack>
          <div>
            <Text size="md" c="dimmed" mb="sm">
              Current Balance
            </Text>
            <Group gap="md" align="baseline">
              <Text size="2rem" fw={700} style={{ lineHeight: 1 }}>
                {formatCredits(currentCredits)}
              </Text>
              <Text size="lg" c="dimmed">
                credits
              </Text>
            </Group>
          </div>
          <div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAddCredits} disabled={isLoading} size="md">
              Add Credits
            </Button>
          </div>
        </Stack>

        {/* Auto Refill Section */}
        <Group
          justify="space-between"
          wrap="nowrap"
          style={{
            border: '1px solid var(--mantine-color-gray-3)',
            padding: '20px',
            borderRadius: '10px',
          }}>
          <Group gap="lg">
            <ThemeIcon
              size={30}
              radius="xl"
              color={autoRefillEnabled ? 'green' : 'gray'}
              variant={autoRefillEnabled ? 'filled' : 'default'}>
              {autoRefillEnabled ? <IconCheck size={20} /> : <IconInfoCircle size={20} />}
            </ThemeIcon>

            <div>
              <Group gap="sm" mb="xs">
                <Text size="lg" fw={600}>
                  Auto Refill
                </Text>
                <Badge color={autoRefillEnabled ? 'green' : 'gray'} variant="light" size="sm">
                  {autoRefillEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </Group>
              <Text size="sm" c="dimmed">
                {getAutoRefillDescription()}
              </Text>
            </div>
          </Group>

          <Button
            variant={autoRefillEnabled ? 'light' : 'filled'}
            size="md"
            onClick={() => setShowPlanModal(true)}
            disabled={isLoading}>
            {autoRefillEnabled ? 'Modify' : 'Enable'}
          </Button>
        </Group>

        {/* Payment Methods Section */}
        <PaymentMethodsSection paymentMethod={paymentMethod} isLoading={isLoading} />

        {/* Additional Info */}
        <Text size="xs" c="dimmed" ta="center" style={{ fontStyle: 'italic' }}>
          Need help with billing? Contact us at{' '}
          <Text component="a" href="mailto:support@sidekickwellness.com" c="blue" td="underline">
            support@sidekickwellness.com
          </Text>
        </Text>
      </Stack>

      {/* Auto-Refill Configuration Modal */}
      <AutoRefillConfigModal
        opened={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        plans={availablePlans}
        currentPlan={currentPlan}
        onPlanUpdate={handleAutoRefillUpdate}
        paymentMethod={paymentMethod}
        currentAutoRefillEnabled={autoRefillEnabled}
        currentThresholdCredits={currentThresholdCredits}
      />

      {/* Credit Purchase Modal */}
      <CreditPurchaseModal
        opened={showCreditPurchaseModal}
        onClose={() => setShowCreditPurchaseModal(false)}
        plans={availablePlans}
        onPurchaseComplete={handleCreditPurchase}
        paymentMethod={paymentMethod}
        currentAutoRefillEnabled={autoRefillEnabled}
        currentCredits={currentCredits}
      />
    </Card>
  )
}
